import type { DocNode } from '@atlaskit/adf-schema';

import { adfNodeToHtml } from './adfNodeToHtml';
import { adfNodeToPlain } from './adfNodeToPlain';
import { buildAdfDocForRichText } from './buildAdfDocForRichText';
import type { ClipboardPayload } from './types';

/**
 * Converts chat message content into a clipboard payload.
 *
 * `content` can be either a Lumina markup string or an ADF DocNode. When streamed
 * Lumina blocks are extracted into `markdownData`, they are spliced back into the
 * markdown before parsing.
 *
 * The resulting ADF is serialized to lightweight attribute-based HTML so ADF editors
 * paste structured nodes (smart links, mentions, lists, etc.). Falls back to plain text
 * if rich conversion fails or produces no content.
 *
 * @param content - The chat message content (Lumina string or ADF DocNode).
 * @param markdownData - Optional map of streamed lumina blocks (keyed `lumina-0`, `lumina-1`, …).
 * @returns ClipboardPayload with plain, html, and format fields.
 */
export const buildClipboardPayload = (
	content: string | DocNode,
	markdownData?: Record<string, string | undefined>,
): ClipboardPayload => {
	try {
		const adfDoc = buildAdfDocForRichText(content, markdownData);
		if (!adfDoc || !adfDoc.content || adfDoc.content.length === 0) {
			// Preserve plain-text fallback when rich conversion produces no insertable content.
			const plain = typeof content === 'string' ? content : adfNodeToPlain(content).trim();
			return { plain, html: null, format: 'plain' };
		}
		const html = adfNodeToHtml(adfDoc);
		const plain = adfNodeToPlain(adfDoc).trim();
		if (!html) {
			return { plain, html: null, format: 'plain' };
		}
		return { plain, html, format: 'rich' };
	} catch {
		const plain = typeof content === 'string' ? content : String(content);
		return { plain, html: null, format: 'plain' };
	}
};
